import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import ExpenseEditModal from '../components/ExpenseEditModal';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import FilterPanel from '../components/FilterPanel';
import toast from 'react-hot-toast';
import { getCategoryTranslator } from '../utils/categoryTranslation';
import { nativeFetch } from '../utils/nativeApi';

const STORAGE_KEY = 'shkalot_expense_filters';

const DEFAULT_FILTERS = {
  search: '',
  categories: [],
  currencies: [],
  dateFrom: null,
  dateTo: null,
  amountMin: null,
  amountMax: null,
};

function ExpensesPage() {
  const { t } = useTranslation(['expenses', 'common', 'categories']);
  const translateCategory = getCategoryTranslator(t);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deletingExpense, setDeletingExpense] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [categories, setCategories] = useState([]);
  
  // Filters and sorting
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : DEFAULT_FILTERS;
  });
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Fetch categories for filter panel
  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch expenses when filters/sort change
  useEffect(() => {
    fetchExpenses(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortBy, sortOrder]);

  // Save filters to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const response = await nativeFetch('/api/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const buildQueryString = useCallback((page) => {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', 25);
    params.append('sort_by', sortBy);
    params.append('sort_order', sortOrder);
    
    if (filters.search) params.append('search', filters.search);
    if (filters.categories?.length) {
      filters.categories.forEach(c => params.append('category', c));
    }
    if (filters.currencies?.length) {
      filters.currencies.forEach(c => params.append('currency', c));
    }
    if (filters.dateFrom) params.append('date_from', filters.dateFrom);
    if (filters.dateTo) params.append('date_to', filters.dateTo);
    if (filters.amountMin !== null) params.append('amount_min', filters.amountMin);
    if (filters.amountMax !== null) params.append('amount_max', filters.amountMax);
    
    return params.toString();
  }, [filters, sortBy, sortOrder]);

  const fetchExpenses = async (page) => {
    setLoading(true);
    try {
      const queryString = buildQueryString(page);
      const response = await nativeFetch(`/api/expenses?${queryString}`);

      if (response.ok) {
        const data = await response.json();
        setExpenses(data.expenses);
        setPagination(data.pagination);
      } else {
        toast.error('Failed to fetch expenses');
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      fetchExpenses(newPage);
    }
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearAllFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.categories?.length) count++;
    if (filters.currencies?.length) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    if (filters.amountMin !== null || filters.amountMax !== null) count++;
    return count;
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? `${cat.icon} ${cat.name}` : categoryId;
  };

  const removeFilter = (filterType, value = null) => {
    const newFilters = { ...filters };
    if (filterType === 'search') newFilters.search = '';
    else if (filterType === 'category') {
      newFilters.categories = filters.categories.filter(c => c !== value);
    }
    else if (filterType === 'currency') {
      newFilters.currencies = filters.currencies.filter(c => c !== value);
    }
    else if (filterType === 'date') {
      newFilters.dateFrom = null;
      newFilters.dateTo = null;
    }
    else if (filterType === 'amount') {
      newFilters.amountMin = null;
      newFilters.amountMax = null;
    }
    setFilters(newFilters);
  };

  const handleDelete = async () => {
    if (!deletingExpense) return;

    try {
      const response = await nativeFetch(`/api/expenses/${deletingExpense.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Expense deleted successfully');
        setDeletingExpense(null);
        // Refresh current page
        fetchExpenses(pagination.page);
      } else {
        toast.error('Failed to delete expense');
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  const handleSave = (updatedExpense) => {
    setExpenses(expenses.map(e => e.id === updatedExpense.id ? updatedExpense : e));
  };

  const handleDeleteFromModal = (deletedId) => {
    // Refresh current page after delete from modal
    fetchExpenses(pagination.page);
  };

  const handleCardClick = (expense) => {
    setEditingExpense(expense);
  };

  const handleDeleteClick = (e, expense) => {
    e.stopPropagation(); // Prevent card click
    setDeletingExpense(expense);
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  const formatDateRange = () => {
    if (!filters.dateFrom && !filters.dateTo) return '';
    const from = filters.dateFrom ? formatDate(filters.dateFrom) : '...';
    const to = filters.dateTo ? formatDate(filters.dateTo) : '...';
    return `${from} - ${to}`;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-2xl font-black">{t('history')}</h1>
          <button
            onClick={() => setShowFilterPanel(true)}
            className="relative px-3 py-2 border-2 border-black rounded-lg font-bold bg-white shadow-hard-sm hover:bg-gray-100 transition-colors flex items-center gap-2"
          >
            🔍 {t('filter')}
            {activeFilterCount > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-primary border-2 border-black rounded-full text-xs font-bold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Sort Controls */}
        <div className="flex items-center gap-2 mb-3 text-sm">
          <span className="font-bold">{t('sort')}:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-2 py-1 border-2 border-black rounded-lg font-bold bg-white"
          >
            <option value="date">{t('date')}</option>
            <option value="amount">{t('amount')}</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
            className="px-2 py-1 border-2 border-black rounded-lg font-bold bg-white hover:bg-gray-100"
          >
            {sortOrder === 'desc' ? `↓ ${t('descending')}` : `↑ ${t('ascending')}`}
          </button>
        </div>

        {/* Active Filter Chips */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {filters.search && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary border-2 border-black rounded-lg text-sm font-bold">
                🔎 "{filters.search}"
                <button onClick={() => removeFilter('search')} className="hover:opacity-70">×</button>
              </span>
            )}
            {filters.categories?.map(catId => (
              <span key={catId} className="inline-flex items-center gap-1 px-2 py-1 bg-primary border-2 border-black rounded-lg text-sm font-bold">
                {getCategoryName(catId)}
                <button onClick={() => removeFilter('category', catId)} className="hover:opacity-70">×</button>
              </span>
            ))}
            {filters.currencies?.map(currency => (
              <span key={currency} className="inline-flex items-center gap-1 px-2 py-1 bg-primary border-2 border-black rounded-lg text-sm font-bold">
                {currency}
                <button onClick={() => removeFilter('currency', currency)} className="hover:opacity-70">×</button>
              </span>
            ))}
            {(filters.dateFrom || filters.dateTo) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary border-2 border-black rounded-lg text-sm font-bold">
                📅 {formatDateRange()}
                <button onClick={() => removeFilter('date')} className="hover:opacity-70">×</button>
              </span>
            )}
            {(filters.amountMin !== null || filters.amountMax !== null) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary border-2 border-black rounded-lg text-sm font-bold">
                💵 ${filters.amountMin || 0} - ${filters.amountMax || '∞'}
                <button onClick={() => removeFilter('amount')} className="hover:opacity-70">×</button>
              </span>
            )}
            <button
              onClick={handleClearAllFilters}
              className="px-2 py-1 text-sm font-bold text-red-500 hover:underline"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Loading indicator */}
        {loading && (
          <div className="text-center py-4 text-lg font-bold">{t('common:messages.loading')}</div>
        )}

        {expenses.length === 0 && !loading ? (
          <div className="card text-center py-8">
            <div className="text-4xl mb-2">{activeFilterCount > 0 ? '🔍' : '📭'}</div>
            <p className="text-lg font-bold mb-1">
              {activeFilterCount > 0 ? t('noMatchingExpenses', 'No matching expenses') : t('noExpenses', 'No expenses yet')}
            </p>
            <p className="text-sm text-gray-600">
              {activeFilterCount > 0 
                ? t('tryAdjustingFilters', 'Try adjusting your filters') 
                : t('startRecording', 'Start recording from the home page!')}
            </p>
            {activeFilterCount > 0 && (
              <button
                onClick={handleClearAllFilters}
                className="mt-3 px-4 py-2 border-2 border-black rounded-lg font-bold bg-white hover:bg-gray-100"
              >
                {t('clearAllFilters', 'Clear all filters')}
              </button>
            )}
          </div>
        ) : !loading && (
          <>
            <div className="space-y-2">
              {expenses.map((expense) => (
                <div 
                  key={expense.id} 
                  className="flex items-center gap-3 p-3 border-2 border-black rounded-lg bg-white shadow-hard-sm cursor-pointer hover:shadow-hard transition-shadow"
                  onClick={() => handleCardClick(expense)}
                >
                  {/* Category Icon */}
                  <div className="text-2xl flex-shrink-0">
                    {expense.category?.icon || '💰'}
                  </div>
                  
                  {/* Main Info */}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-base">
                      {expense.title || 'Untitled'}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
                      <span>{expense.category?.name ? translateCategory(expense.category.name) : t('uncategorized', 'Uncategorized')}</span>
                      <span>•</span>
                      <span>{formatDate(expense.created_at)}</span>
                      {expense.submitted_by && expense.household_id && (
                        <>
                          <span>•</span>
                          <span className="text-primary font-medium">{expense.submitted_by.name}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Amount */}
                  <div className="text-right flex-shrink-0">
                    <span className="font-bold text-base">
                      {expense.amount} {expense.currency}
                    </span>
                  </div>
                  
                  {/* Delete Button */}
                  <button
                    onClick={(e) => handleDeleteClick(e, expense)}
                    className="flex-shrink-0 p-2 text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    ❌
                  </button>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-2 border-2 border-black rounded-lg font-bold bg-white shadow-hard-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← Prev
                </button>
                <span className="px-4 py-2 font-bold">
                  {pagination.page} / {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-2 border-2 border-black rounded-lg font-bold bg-white shadow-hard-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next →
                </button>
              </div>
            )}

            {/* Total count */}
            <div className="text-center text-sm text-gray-500 mt-2">
              {pagination.total} expense{pagination.total !== 1 ? 's' : ''} total
            </div>
          </>
        )}

        {/* Modals */}
        <ExpenseEditModal
          expense={editingExpense}
          isOpen={!!editingExpense}
          onClose={() => setEditingExpense(null)}
          onSave={handleSave}
          onDelete={handleDeleteFromModal}
        />
        
        <DeleteConfirmDialog
          isOpen={!!deletingExpense}
          onConfirm={handleDelete}
          onCancel={() => setDeletingExpense(null)}
        />

        {/* Filter Panel */}
        <FilterPanel
          isOpen={showFilterPanel}
          onClose={() => setShowFilterPanel(false)}
          filters={filters}
          onApply={handleApplyFilters}
          categories={categories}
        />
      </div>
    </DashboardLayout>
  );
}

export default ExpensesPage;
